import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import MessageConsole from "../components/ui/MessageConsole";
import {
  ERROR_TOAST_DURATION_MS,
  isMessageConsolePath,
  MESSAGE_HISTORY_LIMIT,
  SUCCESS_TOAST_DURATION_MS,
  TOAST_PLACEMENT,
} from "../constants/toastConstants";
import "../components/ui/toast.css";

export {
  ERROR_TOAST_DURATION_MS,
  INFO_TOAST_DURATION_MS,
  isMessageConsolePath,
  MESSAGE_CONSOLE_PATHS,
  MESSAGE_HISTORY_LIMIT,
  MESSAGE_HOVER_LIMIT,
  SUCCESS_TOAST_DURATION_MS,
  TOAST_PLACEMENT,
} from "../constants/toastConstants";

const ToastContext = createContext(null);

const resolveDurationMs = (variant, durationMs) => {
  if (Number.isFinite(durationMs) && durationMs >= 0) return durationMs;
  if (variant === "error") return ERROR_TOAST_DURATION_MS;
  return SUCCESS_TOAST_DURATION_MS;
};

export const ToastItem = ({ toast, onDismiss }) => (
  <div className={`app-toast app-toast--${toast.variant}`} role="status">
    <span className="app-toast__message">{toast.message}</span>
    <button
      type="button"
      className="app-toast__close"
      onClick={() => onDismiss(toast.id)}
      aria-label="Dismiss notification"
    >
      ×
    </button>
  </div>
);

export const ToastProvider = ({ children }) => {
  const { pathname } = useLocation();
  const showMessageConsole = isMessageConsolePath(pathname);
  const alignConsoleWithPagination =
    pathname === "/project-administration" ||
    pathname.startsWith("/project-administration/") ||
    pathname === "/collections" ||
    pathname.startsWith("/collections/");
  const [toasts, setToasts] = useState([]);
  const [messageHistory, setMessageHistory] = useState([]);
  const idRef = useRef(0);
  const dismissTimersRef = useRef(new Map());

  const clearDismissTimer = useCallback((id) => {
    const timerId = dismissTimersRef.current.get(id);
    if (timerId != null) {
      clearTimeout(timerId);
      dismissTimersRef.current.delete(id);
    }
  }, []);

  const dismissToast = useCallback(
    (id) => {
      clearDismissTimer(id);
      setToasts((prev) => prev.filter((t) => t.id !== id));
    },
    [clearDismissTimer]
  );

  useEffect(
    () => () => {
      dismissTimersRef.current.forEach((timerId) => clearTimeout(timerId));
      dismissTimersRef.current.clear();
    },
    []
  );

  const showToast = useCallback(
    (
      message,
      {
        variant = "success",
        placement = TOAST_PLACEMENT.DEFAULT,
        durationMs,
      } = {}
    ) => {
      if (!message) return;
      const id = ++idRef.current;
      const resolvedPlacement = placement || TOAST_PLACEMENT.DEFAULT;
      const resolvedVariant = variant || "success";
      const resolvedDuration = resolveDurationMs(resolvedVariant, durationMs);

      setToasts((prev) => [
        ...prev,
        {
          id,
          message,
          variant: resolvedVariant,
          placement: resolvedPlacement,
        },
      ]);

      setMessageHistory((prev) =>
        [
          {
            id,
            message,
            variant: resolvedVariant,
            createdAt: Date.now(),
          },
          ...prev,
        ].slice(0, MESSAGE_HISTORY_LIMIT)
      );

      if (resolvedDuration > 0) {
        const timerId = setTimeout(() => {
          dismissToast(id);
        }, resolvedDuration);
        dismissTimersRef.current.set(id, timerId);
      }
    },
    [dismissToast]
  );

  const value = useMemo(
    () => ({
      showToast,
      dismissToast,
      toasts,
      messageHistory,
    }),
    [showToast, dismissToast, toasts, messageHistory]
  );

  const defaultToasts = toasts.filter(
    (toast) => toast.placement === TOAST_PLACEMENT.DEFAULT
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        className={`app-toast-region${
          alignConsoleWithPagination ? " app-toast-region--with-list-pagination" : ""
        }`}
      >
        <div className="app-toast-container" aria-live="polite" aria-atomic="true">
          {defaultToasts.map((toast) => (
            <ToastItem key={toast.id} toast={toast} onDismiss={dismissToast} />
          ))}
        </div>
        {showMessageConsole ? (
          <div className="app-message-console-row">
            <div id="map-save-status-slot" className="map-save-status-slot" />
            <MessageConsole messageHistory={messageHistory} />
          </div>
        ) : null}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    return {
      showToast: () => {},
      dismissToast: () => {},
      toasts: [],
      messageHistory: [],
    };
  }
  return context;
};
